import { useEffect, useState } from 'preact/hooks';

import getDomain from './get-domain';
import pmem from './pmem';

const ACCT_SUBJECT_RE = /^acct:([^@\s]+@[^@\s]+)$/i;

function normalizeURL(url) {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    urlObj.hash = '';
    urlObj.search = '';
    return urlObj.toString().replace(/\/+$/, '');
  } catch (e) {
    return url.replace(/\/+$/, '');
  }
}

function urlsMatch(a, b) {
  return normalizeURL(a) === normalizeURL(b);
}

function getWebFingerEndpoint(url) {
  try {
    const { protocol, host } = new URL(url);
    return `${protocol}//${host}/.well-known/webfinger`;
  } catch (e) {
    return null;
  }
}

function webFingerMatchesAccount(webFinger, accountURL) {
  const normalizedAccountURL = normalizeURL(accountURL);
  if (!normalizedAccountURL) return false;
  if (urlsMatch(webFinger?.subject, accountURL)) return true;
  if (webFinger?.aliases?.some((alias) => urlsMatch(alias, accountURL))) {
    return true;
  }
  return webFinger?.links?.some((link) => {
    if (link?.rel !== 'self' || !link?.href) return false;
    return urlsMatch(link.href, accountURL);
  });
}

async function fetchWebFingerAcct(accountURL, resource) {
  const endpoint = getWebFingerEndpoint(accountURL);
  if (!endpoint || !resource) return null;

  const response = await fetch(
    `${endpoint}?resource=${encodeURIComponent(resource)}`,
    {
      headers: {
        accept: 'application/jrd+json, application/json',
      },
    },
  );
  if (!response.ok) return null;

  const webFinger = await response.json();
  const [, acct] = webFinger?.subject?.match(ACCT_SUBJECT_RE) || [];
  if (!acct || !webFingerMatchesAccount(webFinger, accountURL)) return null;

  return acct;
}

const memFetchCanonicalAcct = pmem(
  async ({ acct, url }) => {
    if (!url) return null;

    const acctFromURL = await fetchWebFingerAcct(url, url);
    if (acctFromURL) return acctFromURL;

    if (acct) {
      return fetchWebFingerAcct(url, `acct:${acct}`);
    }

    return null;
  },
  {
    maxSize: 200,
    expires: 1000 * 60 * 60,
  },
);

export function getFullAcct(account, instance) {
  const { acct, url } = account || {};
  if (!acct) return '';
  if (acct.includes('@')) return acct;

  const accountInstance = instance || getDomain(url);
  return accountInstance ? `${acct}@${accountInstance}` : acct;
}

export function getDisplayAcct(account, { instance, accountInstance } = {}) {
  const { acct } = account || {};
  if (!acct) return '';
  if (accountInstance && !acct.includes('@')) {
    return `${acct}@${accountInstance}`;
  }
  return acct;
}

export function useAccountAcct(account, options = {}) {
  const { instance, accountInstance, full } = options;
  const fullFallbackAcct = getFullAcct(account, instance || accountInstance);
  const fallbackAcct = full
    ? fullFallbackAcct
    : getDisplayAcct(account, { instance, accountInstance });
  const [resolvedAcct, setResolvedAcct] = useState(fallbackAcct);

  useEffect(() => {
    setResolvedAcct(fallbackAcct);

    if (!account?.url || !account?.acct) return;

    let aborted = false;
    (async () => {
      try {
        const canonicalAcct = await memFetchCanonicalAcct({
          acct: fullFallbackAcct,
          url: account.url,
        });
        if (!aborted && canonicalAcct) {
          const isShortLocalFallback = !full && !fallbackAcct.includes('@');
          const isSameAsFullFallback =
            canonicalAcct.toLowerCase() === fullFallbackAcct.toLowerCase();
          if (!isShortLocalFallback || !isSameAsFullFallback) {
            setResolvedAcct(canonicalAcct);
          }
        }
      } catch (e) {
        // WebFinger lookups can fail due to CORS or server support. Keep API acct.
      }
    })();

    return () => {
      aborted = true;
    };
  }, [
    account?.acct,
    account?.url,
    fallbackAcct,
    full,
    fullFallbackAcct,
    instance,
    accountInstance,
  ]);

  return resolvedAcct;
}
